import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RefreshCw, Wallet, X, BookOpen, ChevronRight, Flame, TrendingUp, Phone, AlertTriangle, Clock, Calendar } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);
}

function fmtINRCompact(n) {
  const num = Math.abs(n || 0);
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(1)}Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)}L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(1)}K`;
  return `₹${num.toFixed(0)}`;
}

function GodownCard({ g }) {
  const filledColor = g.filled >= 0 ? 'text-green-700' : 'text-red-700';
  const mtColor     = g.mt     >= 0 ? 'text-amber-800' : 'text-red-700';

  if (g.isBullet) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="small-label text-gray-400">Godown</div>
          <Flame size={13} className="text-orange-500" />
        </div>
        <div className="font-black text-[15px] tracking-tight mb-3">{g.name}</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="small-label text-gray-400 mb-1">Filled</div>
            <div className={`font-bold text-2xl ${filledColor}`}>{g.filled}</div>
          </div>
          <div>
            <div className="small-label text-gray-400 mb-1">MT</div>
            <div className={`font-bold text-2xl ${mtColor}`}>{g.mt}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="small-label text-gray-400">Godown</div>
        <RefreshCw size={12} className="text-gray-300 hover:text-gray-600 cursor-pointer transition-colors" />
      </div>
      <div className="font-black text-[15px] tracking-tight mb-3">{g.name}</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="small-label text-gray-400 mb-1">Filled</div>
          <div className={`font-bold text-2xl ${filledColor}`}>{g.filled}</div>
        </div>
        <div>
          <div className="small-label text-gray-400 mb-1">MT</div>
          <div className={`font-bold text-2xl ${mtColor}`}>{g.mt}</div>
        </div>
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black text-white text-xs px-3 py-2 rounded shadow-lg">
      <p className="font-semibold mb-0.5">{label}</p>
      <p>₹{payload[0].value}L</p>
    </div>
  );
};

function OverdueWidget({ followups }) {
  const summary  = followups?.summary  || {};
  const allRows  = followups?.rows     || [];

  const totalPending  = summary.total_pending  || 0;
  const overdueCount  = summary.overdue_count  || 0;
  const dueTodayCount = summary.due_today_count || 0;
  const unscheduled   = summary.unscheduled_count || 0;

  // Top rows: overdue first, then due today, then by balance desc
  const urgentRows = [...allRows]
    .sort((a, b) => {
      if (a.days_overdue !== b.days_overdue) return b.days_overdue - a.days_overdue;
      return b.balance - a.balance;
    })
    .slice(0, 5);

  const hasAlerts = overdueCount > 0 || dueTodayCount > 0;

  return (
    <div
      data-testid="overdue-widget"
      className={`mb-5 border rounded-lg p-5 bg-white ${hasAlerts ? 'border-red-200' : 'border-gray-200'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {hasAlerts
            ? <AlertTriangle size={14} className="text-red-500" />
            : <Phone size={14} className="text-gray-400" />
          }
          <div>
            <div className="small-label text-gray-400">Collections</div>
            <div className="font-black text-[15px] tracking-tight">Payment Follow-ups</div>
          </div>
        </div>
        <Link
          to="/followups"
          className="small-label text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
          data-testid="overdue-view-all"
        >
          VIEW ALL <ChevronRight size={10} />
        </Link>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex-1 min-w-[100px] bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
          <div className="small-label text-gray-400 mb-1">Total Pending</div>
          <div className="font-black text-xl tracking-tight text-gray-900">
            {fmtINRCompact(totalPending)}
          </div>
          <div className="text-[11px] text-gray-400 mt-0.5">{summary.count || 0} parties</div>
        </div>

        <div className={`flex-1 min-w-[90px] rounded-lg px-4 py-3 border ${
          overdueCount > 0
            ? 'bg-red-50 border-red-200'
            : 'bg-gray-50 border-gray-100'
        }`}>
          <div className={`small-label mb-1 flex items-center gap-1 ${overdueCount > 0 ? 'text-red-500' : 'text-gray-400'}`}>
            <Clock size={9} /> OVERDUE
          </div>
          <div className={`font-black text-xl tracking-tight ${overdueCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {overdueCount}
          </div>
          <div className={`text-[11px] mt-0.5 ${overdueCount > 0 ? 'text-red-400' : 'text-gray-400'}`}>
            past due date
          </div>
        </div>

        <div className={`flex-1 min-w-[90px] rounded-lg px-4 py-3 border ${
          dueTodayCount > 0
            ? 'bg-amber-50 border-amber-200'
            : 'bg-gray-50 border-gray-100'
        }`}>
          <div className={`small-label mb-1 flex items-center gap-1 ${dueTodayCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            <Calendar size={9} /> DUE TODAY
          </div>
          <div className={`font-black text-xl tracking-tight ${dueTodayCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            {dueTodayCount}
          </div>
          <div className={`text-[11px] mt-0.5 ${dueTodayCount > 0 ? 'text-amber-500' : 'text-gray-400'}`}>
            follow up today
          </div>
        </div>

        {unscheduled > 0 && (
          <div className="flex-1 min-w-[90px] bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
            <div className="small-label text-gray-400 mb-1">UNSCHEDULED</div>
            <div className="font-black text-xl tracking-tight text-gray-500">{unscheduled}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">no date set</div>
          </div>
        )}
      </div>

      {/* Top urgent customers */}
      {urgentRows.length > 0 ? (
        <div className="border-t border-gray-100 pt-3 space-y-0" data-testid="overdue-rows">
          {urgentRows.map((r, i) => {
            const isOverdue   = r.days_overdue > 0;
            const isDueToday  = r.due_date && !isOverdue && r.next_followup_date === new Date().toISOString().slice(0,10);
            return (
              <div
                key={r.party_id}
                className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50 -mx-1 px-1 rounded transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-4 text-[10px] font-bold text-gray-300 text-right flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-gray-800 truncate">{r.party_name}</div>
                    <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                      {isOverdue && (
                        <span className="text-red-500 font-medium">{r.days_overdue}d overdue</span>
                      )}
                      {!isOverdue && r.next_followup_date && (
                        <span className="text-amber-600 font-medium">due {r.next_followup_date}</span>
                      )}
                      {!r.next_followup_date && (
                        <span className="text-gray-400">no date</span>
                      )}
                      {r.last_comment && (
                        <span className="text-gray-300">·</span>
                      )}
                      {r.last_comment && (
                        <span className="truncate max-w-[120px]">{r.last_comment}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className={`font-bold text-[13px] ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                    {fmtINRCompact(r.balance)}
                  </span>
                  <Link
                    to="/followups"
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                      isOverdue
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    Call
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border-t border-gray-100 pt-4 text-center text-xs text-gray-400">
          No outstanding payments. All collections are up to date.
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { authHeader, role } = useAuth();
  const [regAlert, setRegAlert] = useState(true);
  const [summary, setSummary]   = useState(null);
  const [chart, setChart]       = useState([]);
  const [perf, setPerf]         = useState([]);
  const [followups, setFollowups] = useState(null);
  const [loading, setLoading]   = useState(true);

  const isAdmin = role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/dashboard/summary`,           { headers: authHeader() }),
        axios.get(`${API}/dashboard/sales-chart`,       { headers: authHeader() }),
        axios.get(`${API}/analytics/customers?limit=5`, { headers: authHeader() }),
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API}/followups?status=open`, { headers: authHeader() }));
      }
      const results = await Promise.all(requests);
      setSummary(results[0].data);
      setChart(results[1].data);
      setPerf(results[2].data);
      if (isAdmin && results[3]) setFollowups(results[3].data);
    } catch (err) {
      console.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const godowns   = summary?.godowns    || [];
  const toReceive = summary?.cash_flow?.to_receive || 0;
  const toPay     = summary?.cash_flow?.to_pay     || 0;
  const kpis      = summary?.kpis       || {};

  const now        = new Date();
  const monthLabel = now.toLocaleString('en-IN', { month: 'short', year: '2-digit' }).toUpperCase();
  const projected  = (kpis.avg_per_day || 0) * 30;

  if (loading) {
    return (
      <div className="px-6 py-6 max-w-screen-xl mx-auto flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-screen-xl mx-auto">
      {regAlert && (
        <div className="flex items-start gap-3 mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <BookOpen size={15} className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="small-label text-red-600 mb-0.5">Physical Register · Attention</div>
            <div className="text-[13px] text-red-700 font-medium">
              2 register entries don't tally with system
            </div>
            <div className="text-xs text-red-500 mt-0.5">1 qty mismatch · 1 not found in system</div>
          </div>
          <div className="flex items-center gap-1.5">
            <ChevronRight size={14} className="text-red-400 cursor-pointer hover:text-red-600" />
            <button onClick={() => setRegAlert(false)} className="text-red-300 hover:text-red-600 transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Godown cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {godowns.length === 0
          ? <div className="col-span-5 text-sm text-gray-400 py-4">No godowns configured. Add godowns via Super Admin.</div>
          : godowns.map((g, i) => <GodownCard key={i} g={g} />)
        }
      </div>

      {/* Cash Flow + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex items-center justify-between mb-1">
            <div className="small-label text-gray-400">Cash Flow</div>
            <Wallet size={15} className="text-gray-300" />
          </div>
          <div className="font-black text-lg tracking-tight mb-4">Pending ₹</div>
          <div className="space-y-4">
            <div>
              <div className="small-label text-gray-400 mb-1">To Receive</div>
              <div className="font-bold text-xl text-green-700">{fmtINR(toReceive)}</div>
            </div>
            <div>
              <div className="small-label text-gray-400 mb-1">To Pay</div>
              <div className="font-bold text-xl text-red-700">{fmtINR(toPay)}</div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <button className="small-label text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
              VIEW ALL <ChevronRight size={10} />
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex items-center justify-between mb-1">
            <div className="small-label text-gray-400">Sales Analytics</div>
            <button onClick={load} className="small-label text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
              REFRESH <ChevronRight size={10} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <div className="font-black text-lg tracking-tight">Last 7 days</div>
          </div>
          <p className="text-[11px] text-gray-400 mb-3">Sales in ₹ Lakhs — live data</p>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chart} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="" vertical={false} stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false} tickLine={false}
                tickFormatter={v => `${v}L`}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="value" fill="#0a0a0a" radius={[3, 3, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="small-label text-gray-400 mb-2">AVG / DAY — {monthLabel}</div>
          <div className="font-black text-xl tracking-tight mb-1">{fmtINR(kpis.avg_per_day)}</div>
          <div className="text-xs text-gray-500">{kpis.month_days || 0} days so far this month</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="small-label text-gray-400 mb-2">TOTAL — {monthLabel}</div>
          <div className="font-black text-xl tracking-tight mb-1">{fmtINR(kpis.month_sales)}</div>
          <div className="text-xs text-gray-500">{kpis.month_days || 0} of 30d</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="small-label text-gray-400 mb-2">PROJECTED — {monthLabel}</div>
          <div className="font-black text-xl tracking-tight mb-1">{fmtINR(projected)}</div>
          <div className="text-xs text-gray-500">Avg/day × 30 days</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="small-label text-gray-400 mb-2">TO RECEIVE</div>
          <div className="font-black text-xl tracking-tight mb-1 text-green-700">{fmtINR(toReceive)}</div>
          <div className="text-xs text-gray-500">Outstanding from customers</div>
        </div>
      </div>

      {/* Overdue Payment Widget — admin only */}
      {isAdmin && followups && (
        <OverdueWidget followups={followups} />
      )}

      {/* Top 5 Customer Performance Widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label text-gray-400 mb-0.5 flex items-center gap-1.5">
                <TrendingUp size={11} /> Performance
              </div>
              <div className="font-black text-[15px] tracking-tight">Top Customers</div>
            </div>
            <Link to="/performance" className="small-label text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
              VIEW ALL <ChevronRight size={10} />
            </Link>
          </div>
          {perf.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No sales data yet.</p>
          ) : (
            <div className="space-y-3">
              {perf.map((c, i) => {
                const pct = Math.round((c.total_revenue / (perf[0]?.total_revenue || 1)) * 100);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-4 text-[11px] font-bold text-gray-300 text-right flex-shrink-0">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[12px] font-medium text-gray-800 truncate">{c.customer_name}</span>
                        <span className="text-[12px] font-bold text-gray-900 ml-2 flex-shrink-0">{fmtINR(c.total_revenue)}</span>
                      </div>
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-900 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label text-gray-400 mb-0.5">Quick Links</div>
              <div className="font-black text-[15px] tracking-tight">Common Actions</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'New Sale', to: '/entry/sale' },
              { label: 'New Purchase', to: '/entry/purchase' },
              { label: 'New Payment', to: '/entry/payment' },
              { label: 'Godown Transfer', to: '/entry/transfer' },
              { label: 'Pending Payments', to: '/pending/payments' },
              { label: 'MT Tracking', to: '/pending/mt' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-md text-[12px] font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
              >
                {item.label}
                <ChevronRight size={12} className="text-gray-400" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
